import React, { useState, useEffect } from 'react';
import { api } from '../../api/client';
import { useNavigate } from 'react-router-dom';
import {
  Edit, Settings, Trash2, Eye, Plus, Package,
  ShoppingBag, TrendingUp, Users, DollarSign,
  Key, Download, Calendar, CreditCard, XCircle, CheckCircle, ArrowUpRight, Code, ChevronDown, ChevronUp, AlertTriangle
} from 'lucide-react';
import { getPayloadSchema } from '../../constants/writebackPayloads';
import { useCompany } from '../../contexts/CompanyContext';

export default function MyApps() {
  const { activeCompanyId } = useCompany();
  const [activeTab, setActiveTab] = useState('created');
  const [apps, setApps] = useState([]);
  const [purchasedApps, setPurchasedApps] = useState([]); // ADD THIS
  const [appAnalytics, setAppAnalytics] = useState({}); // ADD THIS
  const [showAnalytics, setShowAnalytics] = useState(null); // ADD THIS
  const [loading, setLoading] = useState(true);
  const [selectedApp, setSelectedApp] = useState(null);
  const [webhooks, setWebhooks] = useState([]);
  const [eventTypes, setEventTypes] = useState([]);
  const [webhookForm, setWebhookForm] = useState({
    event_type: '',
    target_url: '',
    secret: '',
    is_active: true
  });
  const [webhookLogs, setWebhookLogs] = useState([]);
  const [showLogs, setShowLogs] = useState(false);
  const [writebackLogs, setWritebackLogs] = useState([]);
  const [showWritebackLogs, setShowWritebackLogs] = useState(false);
  const [allowedEvents, setAllowedEvents] = useState([]);
  const [selectedWebhookUrl, setSelectedWebhookUrl] = useState('');
  const [selectedSecret, setSelectedSecret] = useState('');
  const [selectedEventTypes, setSelectedEventTypes] = useState([]);
  // Writeback events state
  const [writebackEvents, setWritebackEvents] = useState([]);
  const [showWriteback, setShowWriteback] = useState(null);
  const [selectedWritebackEventTypes, setSelectedWritebackEventTypes] = useState([]);
  const [configuredWritebacks, setConfiguredWritebacks] = useState([]);
  const [expandedPayloadWriteback, setExpandedPayloadWriteback] = useState(null);
  // Deactivate modal state
  const [showDeactivateModal, setShowDeactivateModal] = useState(false);
  const [appToDeactivate, setAppToDeactivate] = useState(null);
  const [deactivating, setDeactivating] = useState(false);
  // const [apiKeys, setApiKeys] = useState([]);
  const navigate = useNavigate();

  useEffect(() => {
    fetchApps();
    fetchPurchasedApps();
    fetchEventTypes();
    // fetchApiKeys();
  }, [activeCompanyId]);

  useEffect(() => {
    if (selectedApp) {
      fetchWebhooks(selectedApp);
    }
  }, [selectedApp]);

  useEffect(() => {
    // Fetch allowed event types for current user
    api.get('/api/event-types').then(res => {
      setAllowedEvents(res.data);
    });
  }, []);

  const fetchApps = async () => {
    try {
      const res = await api.get('/api/apps');
      setApps(res.data);
    } catch (err) {
      console.error('Failed to fetch apps:', err);
    } finally {
      setLoading(false);
    }
  };

  const fetchPurchasedApps = async () => {
    try {
      const response = await api.get('/api/stripe/my-purchases');
      setPurchasedApps(response.data);
    } catch (error) {
      console.error('Failed to fetch purchased apps:', error);
    }
  };

  const fetchAppAnalytics = async (appId) => {
    try {
      const res = await api.get(`/api/apps/${appId}/analytics`);
      setAppAnalytics(prev => ({ ...prev, [appId]: res.data }));
      setShowAnalytics(appId);
    } catch (err) {
      console.error('Failed to fetch analytics:', err);
    }
  };

  const fetchEventTypes = async () => {
    try {
      const res = await api.get('/api/event-types');
      setEventTypes(res.data || []);
      if (res.data && res.data.length > 0) {
        setWebhookForm(prev => ({ ...prev, event_type: res.data[0] }));
      }
    } catch (err) {
      console.error('Failed to fetch event types:', err);
    }
  };

  const fetchWebhooks = async (appId) => {
    try {
      const res = await api.get(`/api/apps/${appId}/webhooks`);
      setWebhooks(res.data || []);
    } catch (err) {
      console.error('Failed to fetch webhooks:', err);
      setWebhooks([]);
    }
  };

  const fetchWritebackEvents = async (appId) => {
    try {
      const res = await api.get(`/api/apps/${appId}/writeback-events`);
      // Hide patient.record.deleted event for writeback functionality
      const events = (res.data.allowed_writeback_events || []).filter(
        event => event !== 'patient.record.deleted'
      );
      setWritebackEvents(events);
    } catch (err) {
      console.error('Failed to fetch writeback events:', err);
      setWritebackEvents([]);
    }
  };

  const fetchConfiguredWritebacks = async (appId) => {
    try {
      const res = await api.get(`/api/apps/${appId}/writebacks`);
      setConfiguredWritebacks(res.data || []);
    } catch (err) {
      console.error('Failed to fetch configured writebacks:', err);
      setConfiguredWritebacks([]);
    }
  };

  const handleCreateWriteback = async (appId) => {
    if (selectedWritebackEventTypes.length === 0) {
      alert('Please select at least one event type');
      return;
    }

    try {
      const response = await api.post(`/api/apps/${appId}/writebacks`, {
        event_types: selectedWritebackEventTypes
      });

      const { full_url, secret, created_count, event_types, message } = response.data;
      
      if (created_count > 0) {
        alert(`${message}\n\n• Total Events: ${event_types.join(', ')}\n• URL: ${full_url}\n• Secret: ${secret.substring(0, 12)}...\n\nCopy the secret now - it won't be shown in full again!`);
      } else {
        alert(`${message}\n\nAll selected events were already configured.`);
      }

      // Refresh configured writebacks
      fetchConfiguredWritebacks(appId);
      
      // Reset selection
      setSelectedWritebackEventTypes([]);
    } catch (err) {
      console.error(err);
      alert('Failed to create writeback: ' + (err.response?.data?.detail || err.message));
    }
  };

  const handleDeleteWritebackEvent = async (writebackId, appId, eventType) => {
    if (!window.confirm(`Are you sure you want to remove the event "${eventType}" from this writeback?`)) return;
    try {
      await api.delete(`/api/writebacks/${writebackId}/events/${encodeURIComponent(eventType)}`);
      fetchConfiguredWritebacks(appId);
    } catch (err) {
      alert('Failed to delete writeback event: ' + (err.response?.data?.detail || err.message));
    }
  };

  const handleDeleteWriteback = async (writebackId, appId) => {
    if (!window.confirm('Are you sure you want to delete this entire writeback configuration?')) return;
    try {
      await api.delete(`/api/writebacks/${writebackId}`);
      fetchConfiguredWritebacks(appId);
    } catch (err) {
      alert('Failed to delete writeback');
    }
  };

  const handleDeactivate = async (id) => {
    // Find the app to show in modal
    const app = apps.find(a => a.id === id);
    setAppToDeactivate(app);
    setShowDeactivateModal(true);
  };

  const confirmDeactivate = async () => {
    if (!appToDeactivate) return;
    
    setDeactivating(true);
    try {
      const response = await api.patch(`/api/apps/${appToDeactivate.id}/deactivate`);
      await fetchApps();
      if (selectedApp === appToDeactivate.id) {
        setSelectedApp(null);
        setWebhooks([]);
      }
      setShowDeactivateModal(false);
      setAppToDeactivate(null);
      
      const deactivatedCount = response.data?.subscribers_deactivated || 0;
      if (deactivatedCount > 0) {
        alert(`App deactivated successfully. ${deactivatedCount} subscriber(s) have been deactivated.`);
      } else {
        alert('App deactivated successfully');
      }
    } catch (err) {
      alert('Failed to deactivate the app');
    } finally {
      setDeactivating(false);
    }
  };

  const handleReactivate = async (id) => {
    if (!window.confirm('Are you sure you want to reactivate this app? It will be visible in the marketplace again and all previously deactivated subscribers will be restored.')) return;
    try {
      const response = await api.patch(`/api/apps/${id}/reactivate`);
      await fetchApps();
      
      const reactivatedCount = response.data?.subscribers_reactivated || 0;
      if (reactivatedCount > 0) {
        alert(`App reactivated successfully. ${reactivatedCount} subscriber(s) have been restored.`);
      } else {
        alert('App reactivated successfully');
      }
    } catch (err) {
      alert('Failed to reactivate the app');
    }
  };

  const handleEditApp = (app) => {
    const isMarketplaceApp = app.type === 'public' ||
      app.tagline ||
      app.pricing_model ||
      app.key_features?.length > 0;

    if (isMarketplaceApp) {
      navigate(`/dashboard/submit-app?mode=edit&appId=${app.id}`);
    } else {
      navigate(`/dashboard/submit-app?mode=edit&appId=${app.id}&simple=true`);
    }
  };

  const handleAddWebhook = async () => {
    if (!selectedApp || !webhookForm.target_url || !webhookForm.event_type) {
      alert('Please fill in all required fields');
      return;
    }

    try {
      await api.post(`/api/apps/${selectedApp}/webhooks`, webhookForm);
      setWebhookForm({
        event_type: eventTypes[0] || '',
        target_url: '',
        secret: '',
        is_active: true
      });
      await fetchWebhooks(selectedApp);
    } catch (err) {
      alert('Failed to create a webhook');
    }
  };

  const handleDeleteWebhook = async (webhookId) => {
    if (!window.confirm('Are you sure you want to delete this webhook?')) return;
    try {
      await api.delete(`/api/webhooks/${webhookId}`);
      await fetchWebhooks(selectedApp);
    } catch (err) {
      alert('Failed to delete the webhook');
    }
  };

  const viewWebhookLogs = async (appId) => {
    try {
      const res = await api.get(`/api/apps/${appId}/webhook-logs`);
      setWebhookLogs(res.data);
      setShowLogs(true);
    } catch (err) {
      console.error('Failed to fetch webhook logs:', err);
      alert('Failed to load webhook logs');
    }
  };

  const viewWritebackLogs = async (appId) => {
    try {
      const res = await api.get(`/api/apps/${appId}/writeback-logs`);
      setWritebackLogs(res.data);
      setShowWritebackLogs(true);
    } catch (err) {
      console.error('Failed to fetch writeback logs:', err);
      alert('Failed to load writeback logs');
    }
  };

  const replayWebhook = async (logId) => {
    if (!window.confirm('Replay this webhook delivery? This will re-send the original payload to the webhook URL.')) {
      return;
    }
    try {
      const res = await api.post(`/api/webhook-logs/${logId}/replay`);
      const data = res.data;
      if (data.success) {
        alert(`✅ Replay successful!\n\nStatus: ${data.status_code}\nResponse Time: ${data.response_time_ms}ms\nNew Log ID: ${data.replay_log_id}`);
      } else {
        alert(`⚠️ Replay completed with errors.\n\nStatus: ${data.status_code}\nResponse: ${data.response?.substring(0, 200) || 'N/A'}`);
      }
      // Refresh logs to show the new replay entry
      if (selectedApp) {
        viewWebhookLogs(selectedApp);
      }
    } catch (err) {
      console.error('Failed to replay webhook:', err);
      alert('Failed to replay webhook: ' + (err.response?.data?.detail || err.message));
    }
  };

  const testWebhook = async (appId, eventType) => {
    try {
      const testData = {
        app_id: appId,
        event_type: eventType,
        data: {
          test: true,
          timestamp: new Date().toISOString(),
          message: "Test webhook event"
        }
      };

      const res = await api.post('/api/simulate-event', testData);
      alert(`Test event sent! Check your webhook endpoint.\n${JSON.stringify(res.data.summary, null, 2)}`);
    } catch (err) {
      alert('Failed to send the test event');
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-gray-900"></div>
      </div>
    );
  }

  return (
    <div className="space-y-4 sm:space-y-6">
      <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-3">
        <h1 className="text-2xl sm:text-3xl font-bold">My Apps</h1>
        <div className="flex flex-col sm:flex-row gap-2 sm:gap-3">
           <button
            onClick={() => navigate('/dashboard/submit-app?mode=simple')}
            className="bg-blue-600 text-white px-3 sm:px-4 py-2 rounded hover:bg-blue-700 flex items-center justify-center gap-2 text-sm sm:text-base"
          >
            <Plus className="w-4 h-4" />
            Custom App Submission
          </button> 
          <button
            onClick={() => navigate('/dashboard/submit-app')}
            className="bg-green-600 text-white px-3 sm:px-4 py-2 rounded hover:bg-green-700 flex items-center justify-center gap-2 text-sm sm:text-base"
          >
            <Plus className="w-4 h-4" />
            Ready to Market App Submission
          </button>
        </div>
      </div>

      {/* Tab Navigation */}
      <div className="border-b overflow-x-auto">
        <nav className="flex space-x-4 sm:space-x-8 min-w-max">
          <button
            onClick={() => setActiveTab('created')}
            className={`py-2 px-1 border-b-2 font-medium text-sm ${activeTab === 'created'
              ? 'border-blue-500 text-blue-600'
              : 'border-transparent text-gray-500 hover:text-gray-700'
              }`}
          >
            <div className="flex items-center gap-2">
              <Package className="w-4 h-4" />
              Apps I Created ({apps.length})
            </div>
          </button>
          <button
            onClick={() => setActiveTab('purchased')}
            className={`py-2 px-1 border-b-2 font-medium text-sm ${activeTab === 'purchased'
              ? 'border-blue-500 text-blue-600'
              : 'border-transparent text-gray-500 hover:text-gray-700'
              }`}
          >
            <div className="flex items-center gap-2">
              <ShoppingBag className="w-4 h-4" />
              Apps I Purchased ({purchasedApps.length})
            </div>
          </button>
        </nav>
      </div>

      {/* Tab Content */}
      {activeTab === 'created' ? (
        // Created Apps Tab
        apps.length === 0 ? (
          <div className="bg-white p-6 sm:p-8 rounded-lg shadow text-center">
            <p className="text-gray-600 mb-4 text-sm sm:text-base">You haven't registered any apps yet.</p>
            <p className="text-xs sm:text-sm text-gray-500">
              Use "Custom App" for internal apps or "Market App" for public apps.
            </p>
          </div>
        ) : (
          <div className="space-y-4">
            {apps.map((app) => (
              <div key={app.id} className="bg-white p-4 sm:p-6 rounded-lg shadow">
                <div className="flex flex-col sm:flex-row sm:justify-between sm:items-start gap-3">
                  <div className="flex-1 min-w-0">
                    <div className="flex flex-wrap items-center gap-2 mb-2">
                      <h3 className="text-lg sm:text-xl font-semibold truncate">{app.name}</h3>

                      {/* App Type Badge */}
                      <span className={`px-2 py-1 rounded text-xs font-medium ${app.type === 'internal'
                        ? 'bg-gray-100 text-gray-700 border border-gray-300'
                        : app.type === 'partner'
                          ? 'bg-purple-100 text-purple-700 border border-purple-300'
                          : 'bg-blue-100 text-blue-700 border border-blue-300'
                        }`}>
                        {app.type === 'internal' && '🔧 '}
                        {app.type === 'partner' && '🤝 '}
                        {app.type === 'public' && '🌐 '}
                        {app.type?.charAt(0).toUpperCase() + app.type?.slice(1) || 'Internal'}
                      </span>

                      {/* Approval Status */}
                      {app.type === 'public' && app.is_approved !== undefined && (
                        <span className={`px-2 py-1 rounded text-xs ${app.is_approved
                          ? 'bg-green-100 text-green-800'
                          : 'bg-yellow-100 text-yellow-800'
                          }`}>
                          {app.is_approved ? 'Approved' : 'Pending Review'}
                        </span>
                      )}

                      {/* Active/Deactivated Status */}
                      {app.status !== undefined && (
                        <span className={`px-2 py-1 rounded text-xs ${app.status === 1
                          ? 'bg-blue-100 text-blue-800'
                          : 'bg-gray-100 text-gray-800'
                          }`}>
                          {app.status === 1 ? 'Active' : 'Deactivated'}
                        </span>
                      )}
                    </div>

                    <p className="text-gray-600 mb-2 text-sm sm:text-base line-clamp-2">
                      {app.short_description || app.description || 'No description'}
                    </p>

                    <div className="flex flex-wrap gap-2 sm:gap-4 text-xs sm:text-sm text-gray-500">
                      <span>Created: {new Date(app.created_at).toLocaleDateString()}</span>
                      {app.type === 'internal' && (
                        <a
                          href={`/dashboard/apps/${app.id}/subscribers`}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="flex items-center gap-1 text-blue-600 hover:text-blue-800 hover:underline cursor-pointer"
                          title="View subscriber details (opens in new tab)"
                        >
                          <Users className="w-3 h-3" />
                          {app.subscriber_count || 0} subscriber{app.subscriber_count !== 1 ? 's' : ''}
                        </a>
                      )}
                      {app.website_url && (
                        <a href={app.website_url} target="_blank" rel="noreferrer" className="text-blue-600 hover:underline">
                          Website ↗
                        </a>
                      )}
                    </div>

                    {/* Sales Analytics Summary */}
                    {app.type === 'public' && appAnalytics[app.id] && showAnalytics === app.id && (
                      <div className="mt-3 flex flex-wrap gap-3 sm:gap-4 text-xs sm:text-sm">
                        <div className="flex items-center gap-1">
                          <TrendingUp className="w-4 h-4 text-green-600" />
                          <span>{appAnalytics[app.id].total_sales} sales</span>
                        </div>
                        <div className="flex items-center gap-1">
                          <DollarSign className="w-4 h-4 text-green-600" />
                          <span>${appAnalytics[app.id].total_revenue.toFixed(2)}</span>
                        </div>
                        <div className="flex items-center gap-1">
                          <Users className="w-4 h-4 text-blue-600" />
                          <span>{appAnalytics[app.id].customers.length} customers</span>
                        </div>
                      </div>
                    )}
                  </div>

                  {/* Action Buttons - Responsive */}
                  <div className="flex flex-wrap gap-1 sm:gap-2">
                    {app.type === 'public' && (
                      <button
                        onClick={() => {
                          if (showAnalytics === app.id) {
                            setShowAnalytics(null);
                          } else {
                            fetchAppAnalytics(app.id);
                          }
                        }}
                        className="flex items-center gap-1 px-2 sm:px-3 py-1 text-green-600 hover:bg-green-50 rounded text-xs sm:text-sm"
                        title="View Analytics"
                      >
                        <TrendingUp className="w-4 h-4" />
                        <span className="hidden sm:inline">{showAnalytics === app.id ? 'Hide' : 'Analytics'}</span>
                      </button>
                    )}
                    <button
                      onClick={() => handleEditApp(app)}
                      className="flex items-center gap-1 px-2 sm:px-3 py-1 text-blue-600 hover:bg-blue-50 rounded text-xs sm:text-sm"
                      title="Edit App Details"
                    >
                      <Edit className="w-4 h-4" />
                      <span className="hidden sm:inline">Edit</span>
                    </button>
                    {/* Webhooks and Writeback buttons - only for internal type apps */}
                    {app.type === 'internal' && (
                      <>
                        <button
                          onClick={() => setSelectedApp(selectedApp === app.id ? null : app.id)}
                          className="flex items-center gap-1 px-2 sm:px-3 py-1 text-purple-600 hover:bg-purple-50 rounded text-xs sm:text-sm"
                          title="Configure Webhooks"
                        >
                          <Settings className="w-4 h-4" />
                          <span className="hidden sm:inline">{selectedApp === app.id ? 'Hide' : 'Webhooks'}</span>
                        </button>
                        <button
                          onClick={() => {
                            if (showWriteback === app.id) {
                              setShowWriteback(null);
                              setWritebackEvents([]);
                              setConfiguredWritebacks([]);
                              setSelectedWritebackEventTypes([]);
                            } else {
                              setShowWriteback(app.id);
                              fetchWritebackEvents(app.id);
                              fetchConfiguredWritebacks(app.id);
                            }
                          }}
                          className="flex items-center gap-1 px-2 sm:px-3 py-1 text-orange-600 hover:bg-orange-50 rounded text-xs sm:text-sm"
                          title="Configure Writeback"
                        >
                          <ArrowUpRight className="w-4 h-4" />
                          <span className="hidden sm:inline">{showWriteback === app.id ? 'Hide' : 'Writeback'}</span>
                        </button>
                        <button
                          onClick={() => { setSelectedApp(app.id); viewWebhookLogs(app.id); }}
                          className="flex items-center gap-1 px-2 sm:px-3 py-1 text-indigo-600 hover:bg-indigo-50 rounded text-xs sm:text-sm"
                          title="View Webhook Logs"
                        >
                          <Eye className="w-4 h-4" />
                          <span className="hidden sm:inline">Logs</span>
                        </button>
                        <button
                          onClick={() => { setSelectedApp(app.id); viewWritebackLogs(app.id); }}
                          className="flex items-center gap-1 px-2 sm:px-3 py-1 text-teal-600 hover:bg-teal-50 rounded text-xs sm:text-sm"
                          title="View Writeback Logs"
                        >
                          <Eye className="w-4 h-4" />
                          <span className="hidden sm:inline">WB Logs</span>
                        </button>
                      </>
                    )}
                    {app.status === 0 ? (
                      <button
                        onClick={() => handleReactivate(app.id)}
                        className="flex items-center gap-1 px-2 sm:px-3 py-1 text-green-600 hover:bg-green-50 rounded text-xs sm:text-sm"
                        title="Reactivate App"
                      >
                        <CheckCircle className="w-4 h-4" />
                        <span className="hidden sm:inline">Activate</span>
                      </button>
                    ) : (
                      <button
                        onClick={() => handleDeactivate(app.id)}
                        className="flex items-center gap-1 px-2 sm:px-3 py-1 text-orange-600 hover:bg-orange-50 rounded text-xs sm:text-sm"
                        title="Deactivate App"
                      >
                        <XCircle className="w-4 h-4" />
                        <span className="hidden sm:inline">Deactivate</span>
                      </button>
                    )}
                  </div>
                </div>

                {/* Analytics Details Panel */}
                {showAnalytics === app.id && appAnalytics[app.id] && (
                  <div className="mt-4 sm:mt-6 pt-4 sm:pt-6 border-t">
                    <h4 className="text-base sm:text-lg font-semibold mb-3 sm:mb-4">Sales Analytics</h4>

                    {/* Recent Purchases */}
                    <div className="mb-4 sm:mb-6">
                      <h5 className="font-medium mb-2 sm:mb-3 text-sm sm:text-base">Recent Purchases</h5>
                      {appAnalytics[app.id].recent_purchases.length === 0 ? (
                        <p className="text-gray-500 text-sm">No purchases yet</p>
                      ) : (
                        <div className="overflow-x-auto -mx-4 sm:mx-0">
                          <table className="w-full text-xs sm:text-sm min-w-[500px]">
                            <thead className="bg-gray-50">
                              <tr>
                                <th className="p-2 text-left">Customer</th>
                                <th className="p-2 text-left">Amount</th>
                                <th className="p-2 text-left">Date</th>
                                <th className="p-2 text-left">Type</th>
                              </tr>
                            </thead>
                            <tbody className="divide-y">
                              {appAnalytics[app.id].recent_purchases.map((purchase) => (
                                <tr key={purchase.id}>
                                  <td className="p-2">
                                    <div>
                                      <div className="font-medium">{purchase.user_name}</div>
                                      <div className="text-xs text-gray-500">{purchase.user_email}</div>
                                    </div>
                                  </td>
                                  <td className="p-2">${purchase.amount.toFixed(2)}</td>
                                  <td className="p-2">{new Date(purchase.created_at).toLocaleDateString()}</td>
                                  <td className="p-2">
                                    <span className={`px-2 py-1 rounded text-xs ${purchase.payment_method === 'subscription'
                                      ? 'bg-purple-100 text-purple-800'
                                      : 'bg-blue-100 text-blue-800'
                                      }`}>
                                      {purchase.payment_method}
                                    </span>
                                  </td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </div>
                      )}
                    </div>

                    {/* Customer List */}
                    <div>
                      <h5 className="font-medium mb-2 sm:mb-3 text-sm sm:text-base">All Customers ({appAnalytics[app.id].customers.length})</h5>
                      <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-2 sm:gap-3">
                        {appAnalytics[app.id].customers.map((customer) => (
                          <div key={customer.id} className="bg-gray-50 p-2 sm:p-3 rounded">
                            <div className="font-medium text-xs sm:text-sm truncate">{customer.name}</div>
                            <div className="text-xs text-gray-500 truncate">{customer.email}</div>
                            <div className="text-xs text-gray-600 mt-1">
                              {customer.purchase_count} purchase{customer.purchase_count !== 1 ? 's' : ''}
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>
                )}

                {/* Webhook Configuration Section */}
                {selectedApp === app.id && (
                  <div className="mt-4 sm:mt-6 pt-4 sm:pt-6 border-t border-gray-300">
                    <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-3 sm:p-6">
                      <h4 className="text-base sm:text-lg font-semibold mb-4 sm:mb-6 flex items-center gap-2">
                        <Settings className="w-5 h-5 text-blue-600" />
                        Webhooks Configuration
                      </h4>

                      {/* Bulk Webhook Configuration UI */}
                      <div className="bg-gradient-to-r from-blue-50 to-indigo-50 border-2 border-blue-300 rounded-lg p-3 sm:p-6 mb-4 sm:mb-6 shadow-sm">
                        <h5 className="text-sm sm:text-md font-semibold mb-3 sm:mb-4 flex items-center gap-2 text-gray-800">
                          <Plus className="w-4 sm:w-5 h-4 sm:h-5 text-blue-600" />
                          Configure New Webhooks
                        </h5>

                        <div className="space-y-3 sm:space-y-4">
                          <div>
                            <label className="block mb-1 sm:mb-2 font-medium text-xs sm:text-sm text-gray-700">
                              Webhook URL <span className="text-red-500">*</span>
                            </label>
                            <input
                              type="url"
                              value={selectedWebhookUrl}
                              onChange={(e) => setSelectedWebhookUrl(e.target.value)}
                              className="w-full p-2 sm:p-3 border-2 border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 bg-white shadow-sm text-gray-900 text-sm"
                              placeholder="https://your-app.com/webhooks/dentulu"
                            />
                            <p className="text-xs text-gray-600 mt-1">
                              This URL will receive all selected event types
                            </p>
                          </div>

                          <div>
                            <label className="block mb-1 sm:mb-2 font-medium text-xs sm:text-sm text-gray-700">
                              Secret Key <span className="text-gray-500">(optional)</span>
                            </label>
                            <input
                              type="text"
                              value={selectedSecret}
                              onChange={(e) => setSelectedSecret(e.target.value)}
                              className="w-full p-2 sm:p-3 border-2 border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 font-mono text-xs sm:text-sm bg-white shadow-sm text-gray-900"
                              placeholder="Leave empty to auto-generate"
                            />
                            <p className="text-xs text-gray-600 mt-1">
                              If left empty, a secure secret will be automatically generated
                            </p>
                          </div>

                          <div>
                            <label className="block mb-1 sm:mb-2 font-medium text-xs sm:text-sm text-gray-700">
                              Select Event Types <span className="text-red-500">*</span>
                            </label>
                            <p className="text-xs sm:text-sm text-gray-600 mb-2 sm:mb-3">
                              Choose which events will trigger this webhook
                            </p>

                            <div className="grid grid-cols-1 gap-2 max-h-48 sm:max-h-60 overflow-y-auto border-2 border-gray-300 rounded-lg p-2 sm:p-4 bg-white shadow-sm">
                              {allowedEvents.length === 0 ? (
                                <div className="col-span-2 text-center py-8">
                                  <Settings className="w-12 h-12 text-gray-400 mx-auto mb-3" />
                                  <p className="text-gray-500 mb-2 font-medium">No event types available</p>
                                  <p className="text-xs text-gray-400">Contact your administrator to grant event permissions</p>
                                </div>
                              ) : (
                                allowedEvents.map(event => (
                                  <label
                                    key={event}
                                    className="flex items-center gap-3 p-3 hover:bg-blue-50 rounded-lg cursor-pointer transition border border-transparent hover:border-blue-200"
                                  >
                                    <input
                                      type="checkbox"
                                      checked={selectedEventTypes.includes(event)}
                                      onChange={(e) => {
                                        if (e.target.checked) {
                                          setSelectedEventTypes([...selectedEventTypes, event]);
                                        } else {
                                          setSelectedEventTypes(selectedEventTypes.filter(ev => ev !== event));
                                        }
                                      }}
                                      className="w-5 h-5 text-blue-600 rounded focus:ring-2 focus:ring-blue-500 cursor-pointer"
                                    />
                                    <span className="text-sm font-medium text-gray-700">{event}</span>
                                  </label>
                                ))
                              )}
                            </div>
                            <div className="flex items-center justify-between mt-3 px-2">
                              <p className="text-sm text-gray-600">
                                Selected: <span className="font-bold text-blue-600 text-base">{selectedEventTypes.length}</span> event(s)
                              </p>
                              {selectedEventTypes.length > 0 && (
                                <button
                                  onClick={() => setSelectedEventTypes([])}
                                  className="text-sm text-red-600 hover:text-red-700 font-semibold hover:underline"
                                >
                                  Clear All
                                </button>
                              )}
                            </div>
                          </div>

                          <button
                            onClick={async () => {
                              if (!selectedWebhookUrl) {
                                alert('Please enter a webhook URL');
                                return;
                              }

                              if (selectedEventTypes.length === 0) {
                                alert('Please select at least one event type');
                                return;
                              }

                              try {
                                // Use bulk endpoint: ONE URL = ONE SECRET
                                const response = await api.post(`/api/apps/${app.id}/webhooks/bulk`, {
                                  event_types: selectedEventTypes,
                                  target_url: selectedWebhookUrl,
                                  secret: selectedSecret.trim() || undefined,
                                  is_active: true
                                });

                                const { created_count, updated_count, secret_was_generated, shared_secret } = response.data;

                                let message = `Successfully configured ${created_count + updated_count} webhook(s)!`;
                                if (created_count > 0) message += `\n• ${created_count} created`;
                                if (updated_count > 0) message += `\n• ${updated_count} updated`;
                                if (secret_was_generated) {
                                  message += `\n\nA shared secret was auto-generated for this URL. View it in the table below.`;
                                }

                                alert(message);

                                // Refresh webhooks list
                                fetchWebhooks(app.id);

                                // Reset form
                                setSelectedWebhookUrl('');
                                setSelectedSecret('');
                                setSelectedEventTypes([]);
                              } catch (err) {
                                console.error(err);
                                alert('Failed to configure webhooks: ' + (err.response?.data?.detail || err.message));
                              }
                            }}
                            disabled={!selectedWebhookUrl || selectedEventTypes.length === 0}
                            className="w-full bg-blue-600 text-white py-3 px-4 rounded-lg hover:bg-blue-700 disabled:bg-gray-400 disabled:cursor-not-allowed font-semibold transition shadow-md hover:shadow-lg flex items-center justify-center gap-2 text-base"
                          >
                            <Plus className="w-5 h-5" />
                            Create {selectedEventTypes.length > 0 ? selectedEventTypes.length : ''} Webhook{selectedEventTypes.length !== 1 ? 's' : ''}
                          </button>
                        </div>
                      </div>

                      {/* Configured Webhooks List */}
                      <div className="mt-6">
                        <div className="flex items-center justify-between mb-4">
                          <h5 className="text-md font-semibold text-gray-800">Configured Webhooks</h5>
                          {webhooks.length > 0 && (
                            <span className="text-sm text-gray-500 bg-gray-100 px-3 py-1 rounded-full font-medium">
                              {webhooks.length} webhook{webhooks.length !== 1 ? 's' : ''}
                            </span>
                          )}
                        </div>

                        {webhooks.length === 0 ? (
                          <div className="text-center py-16 bg-gray-50 rounded-lg border-2 border-dashed border-gray-300">
                            <Settings className="w-16 h-16 text-gray-400 mx-auto mb-4" />
                            <p className="text-gray-600 font-semibold mb-2 text-lg">No webhooks configured yet</p>
                            <p className="text-sm text-gray-500">
                              Use the form above to configure webhooks for this app
                            </p>
                          </div>
                        ) : (
                          <div className="overflow-x-auto bg-white rounded-lg border-2 border-gray-300 shadow-sm">
                            <table className="w-full text-sm">
                              <thead className="bg-gray-100 border-b-2 border-gray-300">
                                <tr>
                                  <th className="p-4 text-left font-semibold text-gray-800">Event Type</th>
                                  <th className="p-4 text-left font-semibold text-gray-800">Webhook URL</th>
                                  <th className="p-4 text-left font-semibold text-gray-800">Secret Key</th>
                                  <th className="p-4 text-left font-semibold text-gray-800">Status</th>
                                  <th className="p-4 text-left font-semibold text-gray-800">Actions</th>
                                </tr>
                              </thead>
                              <tbody className="divide-y divide-gray-200">
                                {webhooks.map((webhook) => (
                                  <tr key={webhook.id} className="hover:bg-blue-50 transition">
                                    <td className="p-4">
                                      <span className="inline-flex items-center px-3 py-1.5 bg-purple-100 text-purple-800 rounded-md text-xs font-mono font-semibold border border-purple-200">
                                        {webhook.event_type}
                                      </span>
                                    </td>
                                    <td className="p-4">
                                      <a
                                        href={webhook.target_url}
                                        target="_blank"
                                        rel="noreferrer"
                                        className="text-blue-600 hover:text-blue-800 hover:underline text-xs truncate block max-w-xs font-medium"
                                        title={webhook.target_url}
                                      >
                                        {webhook.target_url.length > 50
                                          ? webhook.target_url.substring(0, 50) + '...'
                                          : webhook.target_url}
                                      </a>
                                    </td>
                                    <td className="p-4">
                                      <div className="flex items-center gap-2">
                                        <code className="text-xs text-gray-700 bg-gray-100 px-3 py-1.5 rounded font-mono border border-gray-300">
                                          {webhook.secret ? webhook.secret.substring(0, 12) + '...' : 'None'}
                                        </code>
                                        {webhook.secret && (
                                          <button
                                            onClick={() => {
                                              navigator.clipboard.writeText(webhook.secret);
                                              alert('Secret key copied to the clipboard!');
                                            }}
                                            className="text-gray-500 hover:text-gray-700 p-1 hover:bg-gray-200 rounded"
                                            title="Copy full secret"
                                          >
                                            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" />
                                            </svg>
                                          </button>
                                        )}
                                      </div>
                                    </td>
                                    <td className="p-4">
                                      {webhook.is_active ? (
                                        <span className="inline-flex items-center px-3 py-1 bg-green-100 text-green-800 rounded-full text-xs font-semibold border border-green-200">
                                          <span className="w-2 h-2 bg-green-500 rounded-full mr-2 animate-pulse"></span>
                                          Active
                                        </span>
                                      ) : (
                                        <span className="inline-flex items-center px-3 py-1 bg-gray-100 text-gray-600 rounded-full text-xs font-semibold border border-gray-300">
                                          <span className="w-2 h-2 bg-gray-400 rounded-full mr-2"></span>
                                          Inactive
                                        </span>
                                      )}
                                    </td>
                                    <td className="p-4">
                                      <div className="flex gap-2">
                                        <button
                                          onClick={() => testWebhook(app.id, webhook.event_type)}
                                          className="inline-flex items-center gap-1 px-3 py-1.5 text-green-600 hover:bg-green-100 rounded-md text-xs font-semibold transition border border-green-200 hover:border-green-300"
                                          title="Send test event"
                                        >
                                          <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M14.752 11.168l-3.197-2.132A1 1 0 0010 9.87v4.263a1 1 0 001.555.832l3.197-2.132a1 1 0 000-1.664z" />
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                                          </svg>
                                          Test
                                        </button>
                                        <button
                                          onClick={() => handleDeleteWebhook(webhook.id)}
                                          className="inline-flex items-center gap-1 px-3 py-1.5 text-red-600 hover:bg-red-100 rounded-md text-xs font-semibold transition border border-red-200 hover:border-red-300"
                                          title="Delete webhook"
                                        >
                                          <Trash2 className="w-3.5 h-3.5" />
                                          Delete
                                        </button>
                                      </div>
                                    </td>
                                  </tr>
                                ))}
                              </tbody>
                            </table>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                )}

                {/* Writeback Configuration Section */}
                {showWriteback === app.id && (
                  <div className="mt-4 sm:mt-6 pt-4 sm:pt-6 border-t border-orange-300">
                    <div className="bg-white rounded-lg shadow-sm border border-orange-200 p-3 sm:p-6">
                      <h4 className="text-base sm:text-lg font-semibold mb-4 sm:mb-6 flex items-center gap-2">
                        <ArrowUpRight className="w-5 h-5 text-orange-600" />
                        Writeback Configuration
                      </h4>

                      {/* Create New Writeback UI */}
                      <div className="bg-gradient-to-r from-orange-50 to-amber-50 border-2 border-orange-300 rounded-lg p-3 sm:p-6 mb-4 sm:mb-6 shadow-sm">
                        <h5 className="text-sm sm:text-md font-semibold mb-3 sm:mb-4 flex items-center gap-2 text-gray-800">
                          <Plus className="w-4 sm:w-5 h-4 sm:h-5 text-orange-600" />
                          Configure New Writeback
                        </h5>

                        <p className="text-sm text-gray-700 mb-4">
                          Select events your app will <strong>write back</strong> to the Dentulu ecosystem.
                          A unique URL and secret will be generated for your app.
                        </p>

                        {writebackEvents.length === 0 ? (
                          <div className="text-center py-8 sm:py-12 bg-white rounded-lg border-2 border-dashed border-orange-300">
                            <ArrowUpRight className="w-12 h-12 sm:w-16 sm:h-16 text-orange-300 mx-auto mb-4" />
                            <p className="text-gray-600 font-semibold mb-2 text-base sm:text-lg">No writeback events available</p>
                            <p className="text-xs sm:text-sm text-gray-500">
                              Contact your administrator to enable writeback events for this app.
                            </p>
                          </div>
                        ) : (
                          <div className="space-y-3 sm:space-y-4">
                            <div>
                              <label className="block mb-1 sm:mb-2 font-medium text-xs sm:text-sm text-gray-700">
                                Select Event Types <span className="text-red-500">*</span>
                              </label>
                              <p className="text-xs sm:text-sm text-gray-600 mb-2 sm:mb-3">
                                Choose which events your app will write back to the ecosystem
                              </p>

                              <div className="grid grid-cols-1 gap-2 max-h-48 sm:max-h-60 overflow-y-auto border-2 border-orange-200 rounded-lg p-2 sm:p-4 bg-white shadow-sm">
                                {writebackEvents.map(event => (
                                  <label
                                    key={event}
                                    className="flex items-center gap-3 p-3 hover:bg-orange-50 rounded-lg cursor-pointer transition border border-transparent hover:border-orange-200"
                                  >
                                    <input
                                      type="checkbox"
                                      checked={selectedWritebackEventTypes.includes(event)}
                                      onChange={(e) => {
                                        if (e.target.checked) {
                                          setSelectedWritebackEventTypes([...selectedWritebackEventTypes, event]);
                                        } else {
                                          setSelectedWritebackEventTypes(selectedWritebackEventTypes.filter(ev => ev !== event));
                                        }
                                      }}
                                      className="w-5 h-5 text-orange-600 rounded focus:ring-2 focus:ring-orange-500 cursor-pointer"
                                    />
                                    <span className="text-sm font-medium text-gray-700">{event}</span>
                                  </label>
                                ))}
                              </div>
                              <div className="flex items-center justify-between mt-3 px-2">
                                <p className="text-sm text-gray-600">
                                  Selected: <span className="font-bold text-orange-600 text-base">{selectedWritebackEventTypes.length}</span> event(s)
                                </p>
                                {selectedWritebackEventTypes.length > 0 && (
                                  <button
                                    onClick={() => setSelectedWritebackEventTypes([])}
                                    className="text-sm text-red-600 hover:text-red-700 font-semibold hover:underline"
                                  >
                                    Clear All
                                  </button>
                                )}
                              </div>
                            </div>

                            <button
                              onClick={() => handleCreateWriteback(app.id)}
                              disabled={selectedWritebackEventTypes.length === 0}
                              className="w-full bg-orange-600 text-white py-3 px-4 rounded-lg hover:bg-orange-700 disabled:bg-gray-400 disabled:cursor-not-allowed font-semibold transition shadow-md hover:shadow-lg flex items-center justify-center gap-2 text-base"
                            >
                              <Plus className="w-5 h-5" />
                              Create Writeback ({selectedWritebackEventTypes.length} event{selectedWritebackEventTypes.length !== 1 ? 's' : ''})
                            </button>
                          </div>
                        )}
                      </div>

                      {/* Configured Writebacks List */}
                      <div className="mt-6">
                        <div className="flex items-center justify-between mb-4">
                          <h5 className="text-md font-semibold text-gray-800">Configured Writebacks</h5>
                          {configuredWritebacks.length > 0 && (
                            <span className="text-sm text-gray-500 bg-gray-100 px-3 py-1 rounded-full font-medium">
                              {configuredWritebacks.length} writeback{configuredWritebacks.length !== 1 ? 's' : ''}
                            </span>
                          )}
                        </div>

                        {configuredWritebacks.length === 0 ? (
                          <div className="text-center py-16 bg-gray-50 rounded-lg border-2 border-dashed border-gray-300">
                            <ArrowUpRight className="w-16 h-16 text-gray-400 mx-auto mb-4" />
                            <p className="text-gray-600 font-semibold mb-2 text-lg">No writebacks configured yet</p>
                            <p className="text-sm text-gray-500">
                              Use the form above to configure writebacks for this app
                            </p>
                          </div>
                        ) : (
                          <div className="overflow-x-auto bg-white rounded-lg border-2 border-orange-200 shadow-sm">
                            <table className="w-full text-sm">
                              <thead className="bg-orange-50 border-b-2 border-orange-200">
                                <tr>
                                  <th className="p-4 text-left font-semibold text-gray-800">Event Type</th>
                                  <th className="p-4 text-left font-semibold text-gray-800">Writeback URL</th>
                                  <th className="p-4 text-left font-semibold text-gray-800">Secret Key</th>
                                  <th className="p-4 text-left font-semibold text-gray-800">Status</th>
                                  <th className="p-4 text-left font-semibold text-gray-800">Actions</th>
                                </tr>
                              </thead>
                              <tbody className="divide-y divide-gray-200">
                                {configuredWritebacks.flatMap((wb) => 
                                  (wb.event_types || []).map((eventType, idx) => (
                                    <React.Fragment key={`${wb.id}-${idx}`}>
                                      <tr className="hover:bg-orange-50 transition">
                                        <td className="p-4">
                                          <span className="inline-flex items-center px-3 py-1.5 bg-orange-100 text-orange-800 rounded-md text-xs font-mono font-semibold border border-orange-200">
                                            {eventType}
                                          </span>
                                        </td>
                                        <td className="p-4">
                                          <div className="flex items-center gap-2">
                                            <code className="text-xs text-gray-700 bg-gray-100 px-3 py-1.5 rounded font-mono border border-gray-300 max-w-md truncate" title={wb.writeback_url}>
                                              {wb.writeback_url}
                                            </code>
                                            <button
                                              onClick={() => {
                                                navigator.clipboard.writeText(wb.writeback_url);
                                                alert('URL copied to the clipboard!');
                                              }}
                                              className="text-gray-500 hover:text-gray-700 p-1 hover:bg-gray-200 rounded"
                                              title="Copy URL"
                                            >
                                              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" />
                                              </svg>
                                            </button>
                                          </div>
                                        </td>
                                        <td className="p-4">
                                          <div className="flex items-center gap-2">
                                            <code className="text-xs text-gray-700 bg-gray-100 px-3 py-1.5 rounded font-mono border border-gray-300">
                                              {wb.secret ? wb.secret.substring(0, 12) + '...' : 'None'}
                                            </code>
                                            {wb.secret && (
                                              <button
                                                onClick={() => {
                                                  navigator.clipboard.writeText(wb.secret);
                                                  alert('Secret key copied to the clipboard!');
                                                }}
                                                className="text-gray-500 hover:text-gray-700 p-1 hover:bg-gray-200 rounded"
                                                title="Copy full secret"
                                              >
                                                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" />
                                                </svg>
                                              </button>
                                            )}
                                          </div>
                                        </td>
                                        <td className="p-4">
                                          {wb.is_active ? (
                                            <span className="inline-flex items-center px-3 py-1 bg-green-100 text-green-800 rounded-full text-xs font-semibold border border-green-200">
                                              <span className="w-2 h-2 bg-green-500 rounded-full mr-2 animate-pulse"></span>
                                              Active
                                            </span>
                                          ) : (
                                            <span className="inline-flex items-center px-3 py-1 bg-gray-100 text-gray-600 rounded-full text-xs font-semibold border border-gray-300">
                                              <span className="w-2 h-2 bg-gray-400 rounded-full mr-2"></span>
                                              Inactive
                                            </span>
                                          )}
                                        </td>
                                        <td className="p-4">
                                          <div className="flex items-center gap-2">
                                            <button
                                              onClick={() => setExpandedPayloadWriteback(expandedPayloadWriteback === `${wb.id}-${eventType}` ? null : `${wb.id}-${eventType}`)}
                                              className="inline-flex items-center gap-1 px-3 py-1.5 text-blue-600 hover:bg-blue-100 rounded-md text-xs font-semibold transition border border-blue-200 hover:border-blue-300"
                                              title="View expected payload format"
                                            >
                                              <Code className="w-3.5 h-3.5" />
                                              Payload
                                              {expandedPayloadWriteback === `${wb.id}-${eventType}` ? (
                                                <ChevronUp className="w-3 h-3" />
                                              ) : (
                                                <ChevronDown className="w-3 h-3" />
                                              )}
                                            </button>
                                            <button
                                              onClick={() => handleDeleteWritebackEvent(wb.id, app.id, eventType)}
                                              className="inline-flex items-center gap-1 px-3 py-1.5 text-red-600 hover:bg-red-100 rounded-md text-xs font-semibold transition border border-red-200 hover:border-red-300"
                                              title={`Delete ${eventType} event`}
                                            >
                                              <Trash2 className="w-3.5 h-3.5" />
                                              Delete
                                            </button>
                                          </div>
                                        </td>
                                      </tr>
                                      {/* Expandable Payload Row for this specific event */}
                                      {expandedPayloadWriteback === `${wb.id}-${eventType}` && (
                                        <tr>
                                          <td colSpan="5" className="p-0">
                                            <div className="bg-gray-900 text-gray-100 p-4 border-t border-orange-300">
                                              <div className="flex items-center justify-between mb-3">
                                                <h4 className="text-sm font-semibold text-orange-400">Expected Payload for {eventType}</h4>
                                                <span className="text-xs text-gray-400">POST to your writeback URL with HMAC signature</span>
                                              </div>
                                              <div className="bg-gray-800 rounded-lg p-3">
                                                <div className="flex items-center justify-between mb-2">
                                                  <span className="text-xs font-mono text-orange-300 bg-orange-900/30 px-2 py-1 rounded">
                                                    {eventType}
                                                  </span>
                                                  <button
                                                    onClick={() => {
                                                      const schema = getPayloadSchema(eventType);
                                                      navigator.clipboard.writeText(JSON.stringify(schema, null, 2));
                                                      alert('Payload copied to the clipboard!');
                                                    }}
                                                    className="text-xs text-gray-400 hover:text-white px-2 py-1 hover:bg-gray-700 rounded"
                                                  >
                                                    Copy
                                                  </button>
                                                </div>
                                                <pre className="text-xs font-mono text-green-400 overflow-x-auto whitespace-pre-wrap">
{JSON.stringify(getPayloadSchema(eventType), null, 2)}
                                                </pre>
                                              </div>
                                              <div className="mt-4 pt-3 border-t border-gray-700">
                                                <p className="text-xs text-gray-400 mb-2">
                                                  <strong className="text-gray-300">Headers required:</strong>
                                                </p>
                                                <pre className="text-xs font-mono text-blue-400 bg-gray-800 rounded p-2">
{`X-Writeback-Signature: sha256=<hmac_signature>
X-Writeback-Timestamp: <unix_timestamp>
Content-Type: application/json`}
                                                </pre>
                                                <p className="text-xs text-gray-400 mt-3 mb-2">
                                                  <strong className="text-gray-300">How to generate signature:</strong>
                                                </p>
                                                <pre className="text-xs font-mono text-yellow-400 bg-gray-800 rounded p-2">
{`1. Get current timestamp: Math.floor(Date.now() / 1000)
2. Canonicalize payload: JSON.stringify(payload, Object.keys(payload).sort())
3. Create signing string: "{timestamp}.{canonical_payload}"
4. Generate HMAC: sha256=HMAC-SHA256(secret, signing_string)`}
                                                </pre>
                                              </div>
                                            </div>
                                          </td>
                                        </tr>
                                      )}
                                    </React.Fragment>
                                  ))
                                )}
                              </tbody>
                            </table>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                )}
              </div>
            ))}
          </div>
        )
      ) : (
        // Purchased Apps Tab
        purchasedApps.length === 0 ? (
          <div className="bg-white p-6 sm:p-8 rounded-lg shadow text-center">
            <p className="text-gray-600 mb-4 text-sm sm:text-base">You haven't purchased any apps yet.</p>
            <button
              onClick={() => navigate('/marketplace')}
              className="text-blue-600 hover:underline text-sm sm:text-base"
            >
              Browse Marketplace →
            </button>
          </div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-6">
            {purchasedApps.map((purchase) => (
              <div key={purchase.id} className="bg-white p-4 sm:p-6 rounded-lg shadow">
                <div className="flex items-start gap-3 sm:gap-4 mb-3 sm:mb-4">
                  {purchase.app.icon_url ? (
                    <img src={purchase.app.icon_url} alt="" className="w-10 h-10 sm:w-12 sm:h-12 rounded flex-shrink-0" />
                  ) : (
                    <div className="w-10 h-10 sm:w-12 sm:h-12 bg-gray-200 rounded flex items-center justify-center flex-shrink-0">
                      <Package className="w-5 h-5 sm:w-6 sm:h-6 text-gray-500" />
                    </div>
                  )}
                  <div className="flex-1 min-w-0">
                    <h3 className="font-semibold text-sm sm:text-base truncate">{purchase.app.name}</h3>
                    <p className="text-xs sm:text-sm text-gray-600 line-clamp-2">{purchase.app.description}</p>
                  </div>
                </div>

                <div className="space-y-2 text-sm">
                  <div className="flex items-center justify-between">
                    <span className="text-gray-600">Purchased:</span>
                    <span>{new Date(purchase.created_at).toLocaleDateString()}</span>
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-gray-600">Amount:</span>
                    <span className="font-medium">${purchase.amount.toFixed(2)}</span>
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-gray-600">License:</span>
                    <span className={`px-2 py-1 rounded text-xs ${purchase.license_status === 'active'
                      ? 'bg-green-100 text-green-800'
                      : 'bg-gray-100 text-gray-800'
                      }`}>
                      {purchase.license_status || 'Pending'}
                    </span>
                  </div>

                  {purchase.license_key && (
                    <div className="mt-3 p-3 bg-gray-50 rounded">
                      <div className="flex items-center justify-between mb-1">
                        <span className="text-xs font-medium">License Key:</span>
                        <Key className="w-3 h-3 text-gray-500" />
                      </div>
                      <code className="text-xs font-mono">{purchase.license_key}</code>
                    </div>
                  )}

                  {purchase.stripe_subscription_id && (
                    <div className="flex items-center gap-1 text-xs text-purple-600">
                      <CreditCard className="w-3 h-3" />
                      Subscription Active
                    </div>
                  )}
                </div>

                <div className="mt-4 flex gap-2">
                  <button
                    onClick={() => navigate(`/marketplace/app/${purchase.app.slug}`)}
                    className="flex-1 text-blue-600 hover:bg-blue-50 py-2 rounded text-sm"
                  >
                    View App
                  </button>
                  {purchase.app.documentation_url && (
                    <a
                      href={purchase.app.documentation_url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="flex-1 text-gray-600 hover:bg-gray-50 py-2 rounded text-sm text-center"
                    >
                      Docs
                    </a>
                  )}
                </div>
              </div>
            ))}
          </div>
        )
      )}

      {/* Webhook Logs Modal - Mobile Friendly */}
      {showLogs && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex justify-center items-center z-[9999] p-2 sm:p-4">
          <div className="bg-white w-full max-w-6xl rounded-xl shadow-2xl relative max-h-[95vh] sm:max-h-[90vh] flex flex-col">
            {/* Modal Header */}
            <div className="flex items-center justify-between p-4 sm:p-6 border-b border-gray-200">
              <div>
                <h2 className="text-lg sm:text-2xl font-bold text-gray-800">Webhook Logs</h2>
                <p className="text-xs sm:text-sm text-gray-600 mt-1">
                  {webhookLogs.length} log{webhookLogs.length !== 1 ? 's' : ''} found
                </p>
              </div>
              <button
                onClick={() => setShowLogs(false)}
                className="text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-full p-2 transition"
                title="Close"
              >
                <svg className="w-5 h-5 sm:w-6 sm:h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>

            {/* Modal Content */}
            <div className="flex-1 overflow-y-auto p-3 sm:p-6">
              {webhookLogs.length === 0 ? (
                <div className="text-center py-12 sm:py-16">
                  <div className="inline-flex items-center justify-center w-12 h-12 sm:w-16 sm:h-16 bg-gray-100 rounded-full mb-4">
                    <Eye className="w-6 h-6 sm:w-8 sm:h-8 text-gray-400" />
                  </div>
                  <p className="text-gray-600 font-medium mb-2 text-sm sm:text-base">No webhook logs found</p>
                  <p className="text-xs sm:text-sm text-gray-500">
                    Test your webhooks to see delivery logs here.
                  </p>
                </div>
              ) : (
                <div className="overflow-x-auto -mx-3 sm:mx-0">
                  <table className="w-full text-xs sm:text-sm border-collapse min-w-[600px]">
                    <thead>
                      <tr className="bg-gradient-to-r from-gray-100 to-gray-50 border-b-2 border-gray-300">
                        <th className="p-2 sm:p-4 text-left font-semibold text-gray-800 whitespace-nowrap">
                          Event Type
                        </th>
                        <th className="p-2 sm:p-4 text-left font-semibold text-gray-800 whitespace-nowrap">
                          Target URL
                        </th>
                        <th className="p-2 sm:p-4 text-left font-semibold text-gray-800 whitespace-nowrap">
                          Status
                        </th>
                        <th className="p-2 sm:p-4 text-left font-semibold text-gray-800 whitespace-nowrap">
                          Response Time
                        </th>
                        <th className="p-4 text-left font-semibold text-gray-800 whitespace-nowrap">
                          Response
                        </th>
                        <th className="p-4 text-left font-semibold text-gray-800 whitespace-nowrap">
                          Timestamp
                        </th>
                        <th className="p-4 text-left font-semibold text-gray-800 whitespace-nowrap">
                          Actions
                        </th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-200">
                      {webhookLogs.map((log, index) => (
                        <tr
                          key={log.id || index}
                          className="hover:bg-blue-50 transition-colors"
                        >
                          <td className="p-4">
                            <span className="inline-flex items-center px-2.5 py-1 bg-purple-100 text-purple-800 rounded-md text-xs font-mono font-semibold">
                              {log.event_type || 'N/A'}
                            </span>
                          </td>
                          <td className="p-4">
                            <div className="max-w-xs">
                              <a
                                href={log.target_url}
                                target="_blank"
                                rel="noreferrer"
                                className="text-blue-600 hover:text-blue-800 hover:underline text-xs font-medium truncate block"
                                title={log.target_url}
                              >
                                {log.target_url && log.target_url.length > 40
                                  ? log.target_url.substring(0, 40) + '...'
                                  : log.target_url || 'N/A'}
                              </a>
                            </div>
                          </td>
                          <td className="p-4">
                            <span className={`inline-flex items-center px-3 py-1 rounded-full text-xs font-semibold ${log.status_code >= 200 && log.status_code < 300
                              ? 'bg-green-100 text-green-800 border border-green-200'
                              : log.status_code >= 400 && log.status_code < 500
                                ? 'bg-yellow-100 text-yellow-800 border border-yellow-200'
                                : log.status_code >= 500
                                  ? 'bg-red-100 text-red-800 border border-red-200'
                                  : 'bg-gray-100 text-gray-600 border border-gray-200'
                              }`}>
                              {log.status_code !== undefined && log.status_code !== null ? log.status_code : 'N/A'}
                            </span>
                          </td>
                          <td className="p-4 whitespace-nowrap">
                            <span className={`inline-flex items-center px-2 py-1 rounded text-xs font-mono ${
                              log.response_time_ms != null
                                ? log.response_time_ms < 500
                                  ? 'bg-green-50 text-green-700'
                                  : log.response_time_ms < 3000
                                    ? 'bg-yellow-50 text-yellow-700'
                                    : 'bg-red-50 text-red-700'
                                : 'bg-gray-50 text-gray-500'
                            }`}>
                              {log.response_time_ms != null
                                ? `${Math.round(log.response_time_ms)}ms`
                                : '—'}
                            </span>
                          </td>
                          <td className="p-4">
                            <div className="max-w-xs">
                              <p
                                className="text-xs text-gray-700 truncate font-mono"
                                title={log.response}
                              >
                                {log.response
                                  ? (log.response.length > 50
                                    ? log.response.substring(0, 50) + '...'
                                    : log.response)
                                  : '—'}
                              </p>
                            </div>
                          </td>
                          <td className="p-4 whitespace-nowrap">
                            <div className="text-xs text-gray-600">
                              {log.timestamp
                                ? new Date(log.timestamp).toLocaleString('en-US', {
                                  month: 'short',
                                  day: 'numeric',
                                  year: 'numeric',
                                  hour: '2-digit',
                                  minute: '2-digit',
                                  second: '2-digit'
                                })
                                : 'N/A'}
                            </div>
                          </td>
                          <td className="p-4">
                            <button
                              onClick={() => replayWebhook(log.id)}
                              className="inline-flex items-center gap-1 px-3 py-1.5 bg-blue-600 text-white rounded-md text-xs font-semibold hover:bg-blue-700 transition shadow-sm"
                              title="Replay this webhook delivery"
                            >
                              <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                              </svg>
                              Replay
                            </button>
                          </td>
                          {/* <td className="p-4">
                            <button
                              onClick={() => {
                                // Show detailed log info
                                const details = `
Webhook Log Details
===================

Event Type: ${log.event_type || 'N/A'}
Target URL: ${log.target_url || 'N/A'}
Status Code: ${log.status_code || 'N/A'}
Timestamp: ${log.timestamp ? new Date(log.timestamp).toLocaleString() : 'N/A'}

Payload:
${log.payload ? JSON.stringify(log.payload, null, 2) : 'N/A'}

Response:
${log.response || 'N/A'}
                          `.trim();

                                alert(details);
                              }}
                              className="text-blue-600 hover:text-blue-800 hover:bg-blue-100 px-2 py-1 rounded text-xs font-semibold transition"
                              title="View full details"
                            >
                              View
                            </button>
                          </td> */}
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>

            {/* Modal Footer */}
            <div className="flex items-center justify-between p-6 border-t border-gray-200 bg-gray-50">
              <div className="text-sm text-gray-600">
                {webhookLogs.length > 0 && (
                  <span>
                    Showing {webhookLogs.length} most recent log{webhookLogs.length !== 1 ? 's' : ''}
                  </span>
                )}
              </div>
              <div className="flex gap-2">
                <button
                  onClick={() => {
                    // Refresh logs
                    if (selectedApp) {
                      viewWebhookLogs(selectedApp);
                    }
                  }}
                  className="bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 transition font-semibold text-sm flex items-center gap-2"
                >
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                  </svg>
                  Refresh
                </button>
                <button
                  onClick={() => setShowLogs(false)}
                  className="bg-gray-600 text-white px-4 py-2 rounded-lg hover:bg-gray-700 transition font-semibold text-sm"
                >
                  Close
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Writeback Logs Modal */}
      {showWritebackLogs && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex justify-center items-center z-[9999] p-2 sm:p-4">
          <div className="bg-white w-full max-w-6xl rounded-xl shadow-2xl relative max-h-[95vh] sm:max-h-[90vh] flex flex-col">
            {/* Modal Header */}
            <div className="flex items-center justify-between p-4 sm:p-6 border-b border-gray-200">
              <div>
                <h2 className="text-lg sm:text-2xl font-bold text-gray-800">Writeback Logs</h2>
                <p className="text-xs sm:text-sm text-gray-600 mt-1">
                  {writebackLogs.length} log{writebackLogs.length !== 1 ? 's' : ''} found • Incoming data from external apps
                </p>
              </div>
              <button
                onClick={() => setShowWritebackLogs(false)}
                className="text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-full p-2 transition"
                title="Close"
              >
                <svg className="w-5 h-5 sm:w-6 sm:h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>

            {/* Modal Content */}
            <div className="flex-1 overflow-y-auto p-3 sm:p-6">
              {writebackLogs.length === 0 ? (
                <div className="text-center py-12 sm:py-16">
                  <div className="inline-flex items-center justify-center w-12 h-12 sm:w-16 sm:h-16 bg-gray-100 rounded-full mb-4">
                    <ArrowUpRight className="w-6 h-6 sm:w-8 sm:h-8 text-gray-400" />
                  </div>
                  <p className="text-gray-600 font-medium mb-2 text-sm sm:text-base">No writeback logs found</p>
                  <p className="text-xs sm:text-sm text-gray-500">
                    When external apps send data to your writeback URL, logs will appear here.
                  </p>
                </div>
              ) : (
                <div className="overflow-x-auto -mx-3 sm:mx-0">
                  <table className="w-full text-xs sm:text-sm border-collapse min-w-[600px]">
                    <thead>
                      <tr className="bg-gradient-to-r from-teal-100 to-teal-50 border-b-2 border-teal-300">
                        <th className="p-2 sm:p-4 text-left font-semibold text-gray-800 whitespace-nowrap">
                          Event Type
                        </th>
                        <th className="p-2 sm:p-4 text-left font-semibold text-gray-800 whitespace-nowrap">
                          Status
                        </th>
                        <th className="p-2 sm:p-4 text-left font-semibold text-gray-800 whitespace-nowrap">
                          Response
                        </th>
                        <th className="p-2 sm:p-4 text-left font-semibold text-gray-800 whitespace-nowrap">
                          Response Time
                        </th>
                        <th className="p-4 text-left font-semibold text-gray-800 whitespace-nowrap">
                          Timestamp
                        </th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-200">
                      {writebackLogs.map((log, index) => (
                        <tr
                          key={log.id || index}
                          className="hover:bg-teal-50 transition-colors"
                        >
                          <td className="p-4">
                            <span className="inline-flex items-center px-2.5 py-1 bg-orange-100 text-orange-800 rounded-md text-xs font-mono font-semibold">
                              {log.event_type || 'N/A'}
                            </span>
                          </td>
                          <td className="p-4">
                            <span className={`inline-flex items-center px-3 py-1 rounded-full text-xs font-semibold ${
                              log.status_code >= 200 && log.status_code < 300
                                ? 'bg-green-100 text-green-800 border border-green-200'
                                : log.status_code >= 400 && log.status_code < 500
                                  ? 'bg-yellow-100 text-yellow-800 border border-yellow-200'
                                  : log.status_code >= 500
                                    ? 'bg-red-100 text-red-800 border border-red-200'
                                    : 'bg-gray-100 text-gray-600 border border-gray-200'
                            }`}>
                              {log.status_code !== undefined && log.status_code !== null ? log.status_code : 'N/A'}
                            </span>
                          </td>
                          <td className="p-4">
                            <div className="max-w-xs">
                              <span className="text-xs text-gray-600 truncate block" title={log.response}>
                                {log.response && log.response.length > 50
                                  ? log.response.substring(0, 50) + '...'
                                  : log.response || 'N/A'}
                              </span>
                            </div>
                          </td>
                          <td className="p-4 whitespace-nowrap">
                            <span className={`inline-flex items-center px-2 py-1 rounded text-xs font-mono ${
                              log.response_time_ms != null
                                ? log.response_time_ms < 500
                                  ? 'bg-green-50 text-green-700'
                                  : log.response_time_ms < 3000
                                    ? 'bg-yellow-50 text-yellow-700'
                                    : 'bg-red-50 text-red-700'
                                : 'bg-gray-50 text-gray-500'
                            }`}>
                              {log.response_time_ms != null ? `${Math.round(log.response_time_ms)}ms` : 'N/A'}
                            </span>
                          </td>
                          <td className="p-4 whitespace-nowrap">
                            <span className="text-xs text-gray-600">
                              {log.created_at
                                ? new Date(log.created_at).toLocaleString(undefined, {
                                    month: 'short',
                                    day: 'numeric',
                                    hour: '2-digit',
                                    minute: '2-digit',
                                    second: '2-digit'
                                  })
                                : 'N/A'}
                            </span>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>

            {/* Modal Footer */}
            <div className="flex items-center justify-between p-6 border-t border-gray-200 bg-gray-50">
              <div className="text-sm text-gray-600">
                {writebackLogs.length > 0 && (
                  <span>
                    Showing {writebackLogs.length} most recent log{writebackLogs.length !== 1 ? 's' : ''}
                  </span>
                )}
              </div>
              <div className="flex gap-2">
                <button
                  onClick={() => {
                    if (selectedApp) {
                      viewWritebackLogs(selectedApp);
                    }
                  }}
                  className="bg-teal-600 text-white px-4 py-2 rounded-lg hover:bg-teal-700 transition font-semibold text-sm flex items-center gap-2"
                >
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                  </svg>
                  Refresh
                </button>
                <button
                  onClick={() => setShowWritebackLogs(false)}
                  className="bg-gray-600 text-white px-4 py-2 rounded-lg hover:bg-gray-700 transition font-semibold text-sm"
                >
                  Close
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Deactivate App Confirmation Modal */}
      {showDeactivateModal && appToDeactivate && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex justify-center items-center z-[9999] p-4">
          <div className="bg-white w-full max-w-md rounded-xl shadow-2xl">
            {/* Modal Header */}
            <div className="p-6 border-b border-gray-200">
              <div className="flex items-center gap-4">
                <div className="flex-shrink-0 w-12 h-12 bg-red-100 rounded-full flex items-center justify-center">
                  <AlertTriangle className="w-6 h-6 text-red-600" />
                </div>
                <div>
                  <h2 className="text-xl font-bold text-gray-800">Deactivate App</h2>
                  <p className="text-sm text-gray-500 mt-0.5">{appToDeactivate.name}</p>
                </div>
              </div>
            </div>

            {/* Modal Content */}
            <div className="p-6">
              <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-4">
                <h3 className="font-semibold text-red-800 mb-2 flex items-center gap-2">
                  <AlertTriangle className="w-4 h-4" />
                  Warning: This action will affect subscribers
                </h3>
                <ul className="text-sm text-red-700 space-y-2">

                  <li className="flex items-start gap-2">
                    <span className="text-red-500 mt-0.5">•</span>
                    <span><strong>All existing subscribers will be deactivated</strong> and lose access to the app's API</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="text-red-500 mt-0.5">•</span>
                    <span>Webhooks and writebacks will stop working for all subscribers</span>
                  </li>
                </ul>
              </div>

              <div className="bg-amber-50 border border-amber-200 rounded-lg p-4">
                <p className="text-sm text-amber-800">
                  <strong>Note:</strong> You can reactivate the app later. All previously deactivated subscribers will be automatically restored.
                </p>
              </div>
            </div>

            {/* Modal Footer */}
            <div className="flex items-center justify-end gap-3 p-6 border-t border-gray-200 bg-gray-50 rounded-b-xl">
              <button
                onClick={() => {
                  setShowDeactivateModal(false);
                  setAppToDeactivate(null);
                }}
                disabled={deactivating}
                className="px-4 py-2 text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition font-medium disabled:opacity-50"
              >
                Cancel
              </button>
              <button
                onClick={confirmDeactivate}
                disabled={deactivating}
                className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition font-medium flex items-center gap-2 disabled:opacity-50"
              >
                {deactivating ? (
                  <>
                    <svg className="animate-spin h-4 w-4" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                    </svg>
                    Deactivating...
                  </>
                ) : (
                  <>
                    <XCircle className="w-4 h-4" />
                    Deactivate App
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}